'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';

import { usePageActionsSlotContext } from './PageActionsSlot';

type UseUsecaseLinkOptions = {
  href?: string;
  onClick?: () => void;
  ariaLabel?: string;
  external?: boolean;
};

const QuestionIcon = () => (
  <span
    aria-hidden="true"
    className="inline-flex h-4 w-4 items-center justify-center text-sm font-semibold leading-none"
  >
    ?
  </span>
);

export const useUsecaseLink = ({ href, onClick, ariaLabel, external }: UseUsecaseLinkOptions): void => {
  const { registerUsecase, unregisterUsecase } = usePageActionsSlotContext();
  const idRef = useRef<symbol | null>(null);
  if (idRef.current === null) {
    idRef.current = Symbol('useUsecaseLink');
  }

  const tCommon = useTranslations('common');
  const label = ariaLabel ?? tCommon('viewUsecase');
  const onClickRef = useRef(onClick);

  useEffect(() => {
    onClickRef.current = onClick;
  }, [onClick]);

  const handleClick = useCallback(() => {
    onClickRef.current?.();
  }, []);

  const node = useMemo<ReactNode>(() => {
    const baseClass =
      'btn btn-ghost btn-sm btn-square hover:bg-transparent active:bg-transparent focus:bg-transparent focus-visible:outline-none focus-visible:ring-0 [-webkit-tap-highlight-color:transparent]';

    if (href) {
      if (external) {
        return (
          <a href={href} target="_blank" rel="noreferrer" aria-label={label} title={label} className={baseClass}>
            <QuestionIcon />
          </a>
        );
      }
      return (
        <Link href={href} aria-label={label} title={label} className={baseClass}>
          <QuestionIcon />
        </Link>
      );
    }

    return (
      <button type="button" aria-label={label} title={label} onClick={handleClick} className={baseClass}>
        <QuestionIcon />
      </button>
    );
  }, [href, handleClick, label, external]);

  useEffect(() => {
    const id = idRef.current!;
    registerUsecase(id, node);
    return () => {
      unregisterUsecase(id);
    };
  }, [node, registerUsecase, unregisterUsecase]);
};
